import * as React from "react";
import styled from "@mui/material/styles/styled";
import { useTranslate } from "../../../hooks/useTranslate";
import { usePatient } from "../../../state/patientContext";
import { SelectionType } from "../../uiControllers/AnswerButton";
import { QuestionsIF } from "../QuestionsIF";
import { DocQuestion } from "../DocQuestion";
import { ClosedSingleQuestionView } from "../closedSingleQuestionView";
import { AnswerChip } from "../../../style/chips";
import { SymptomText } from "../../uiControllers/SymptomText";
import { GenericButton } from "../../uiControllers/genericButton";
import { useBackgroundQuestionLogic } from "../hooks/useBackgroundQuestionLogic";
import { MedicationSelectionForm } from "./medicationSelectionForm";
import { QuestionOptionsContainer } from "../layout/questionOptionsContainer";
import { QuestionButtonArea } from "../ClosedMultipleQuestionView";
import ClearIcon from "@mui/icons-material/Clear";
import { PatientColorMap } from "../../../layout/patientColorMap";

//region [[ Styles ]]
const MedicationQuestionView = styled("div")({
  width: "100%",
});

const MedicationChip = styled(AnswerChip)({
  margin: "10px",
  height: "50px",
  fontSize: "12px",
  flexBasis: "30.3%",
  minWidth: "170px", // Tested with "Alpha-adrenoceptor agonist"
  padding: "0 12px",
  "& .patient-MuiChip-label": {
    whiteSpace: "normal",
    textTransform: "capitalize",
  },
  "& .patient-MuiChip-icon": {
    color: PatientColorMap.White,
    margin: "5px",
  },
  backgroundColor: PatientColorMap.Primary,
});

const MedicationChipsContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  flexWrap: "wrap",
  width: "100%",
});
//endregion [[ Styles ]]
//region [[ Props ]]
export interface MedicationQuestionProps extends QuestionsIF {}

//endregion [[ Props ]]
//region [[ Functions ]]
//endregion [[ Functions ]]
export const MedicationQuestion = ({
  onAnswer,
  answer,
  question,
  ...props
}: MedicationQuestionProps) => {
  const { translate } = useTranslate();

  const { gender } = usePatient();

  const askBeforePresentingBackgroundQuestions = false; // Make it hard-codded true for now

  const {
    answerSelectionType,
    handleInitialAnswer,
    answers: medications,
    onRemove,
    onAdd,
  } = useBackgroundQuestionLogic(onAnswer, answer, "medication");

  const initialYesNowWasNotYetAnswered =
    askBeforePresentingBackgroundQuestions &&
    answerSelectionType === null &&
    !answer;

  return (
    <MedicationQuestionView>
      <DocQuestion
        text={translate("MEDICATION_QUESTION")}
        miniView={props.miniView}
      />
      {askBeforePresentingBackgroundQuestions &&
        initialYesNowWasNotYetAnswered && (
          <ClosedSingleQuestionView
            gender={gender}
            onClick={handleInitialAnswer}
          />
        )}
      {(answerSelectionType === SelectionType.YES ||
        !askBeforePresentingBackgroundQuestions) &&
        !answer && (
          <>
            <QuestionOptionsContainer>
              <MedicationChipsContainer>
                {!!medications.length &&
                  medications.map((qf, index) => (
                    <MedicationChip
                      key={qf.id}
                      className={"answer-chip"}
                      label={<SymptomText symptom={qf} />}
                      onClick={() => onRemove(index)}
                      icon={<ClearIcon />}
                      data-selected
                    />
                  ))}
              </MedicationChipsContainer>
              <MedicationSelectionForm
                onAddMedication={onAdd}
                question={question}
                isSecond={!!medications?.length}
              />
            </QuestionOptionsContainer>
            <QuestionButtonArea>
              <GenericButton
                className={"submit-button"}
                label={translate(medications?.length ? "ANSWER_DONE" : "SKIP")}
                onClick={() => onAnswer(medications)}
              />
            </QuestionButtonArea>
          </>
        )}
    </MedicationQuestionView>
  );
};
